import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, MediaAsset } from '../api';
import { mediaEndpoints } from '../api';
import { usePermissions } from '../session';
import { NotPermitted } from '../App';
import { Badge, Button, ErrorNote, Panel } from '../ui';

/**
 * The media library (D-87).
 *
 * One shelf of pictures for the whole platform: event art, sponsor logos, the
 * images inside event content, the artwork on a printed ticket. Every asset
 * says where it is used, and an asset that is used cannot be deleted — the
 * library offers the list of places instead, so nobody has to find the hole
 * on a live page afterward.
 */

const KB = 1024;
const size = (bytes: number) => (bytes < KB * KB ? `${Math.round(bytes / KB)} KB` : `${(bytes / KB / KB).toFixed(1)} MB`);

export default function MediaPage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [category, setCategory] = useState('');
  const [query, setQuery] = useState('');
  const [unused, setUnused] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const categories = useQuery({ queryKey: ['media-categories'], queryFn: mediaEndpoints.categories });
  const assets = useQuery({ queryKey: ['media', category, query, unused], queryFn: () => mediaEndpoints.list({ category: category || null, q: query || null, unused }) });
  const detail = useQuery({ queryKey: ['media-detail', selected], queryFn: () => mediaEndpoints.detail(selected!), enabled: Boolean(selected) });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['media'] });
    if (selected) void queryClient.invalidateQueries({ queryKey: ['media-detail', selected] });
  };
  const fail = (cause: ApiError) => setError(cause.message);

  const upload = useMutation({
    mutationFn: (file: File) => mediaEndpoints.upload(file, { category: category || null, title: file.name }),
    onSuccess: (result) => {
      setError(null);
      setSelected(result.asset.id);
      refresh();
    },
    onError: fail,
  });

  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) => mediaEndpoints.update(id, input),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (id: string) => mediaEndpoints.remove(id),
    onSuccess: () => {
      setSelected(null);
      setError(null);
      refresh();
    },
    onError: fail,
  });

  if (!can('media.view')) return <NotPermitted permission="media.view" />;

  const manage = can('media.manage');
  const asset = detail.data?.asset ?? null;

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-semibold text-ink-900">Media library</h1>
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name" aria-label="Search" className="ml-auto rounded border border-line bg-surface px-2 py-1.5 text-sm" />
        <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category" className="rounded border border-line bg-surface px-2 py-1.5 text-sm">
          <option value="">Every category</option>
          {(categories.data?.categories ?? []).map((row) => (
            <option key={row.code} value={row.code}>
              {row.name}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-ink-700">
          <input type="checkbox" checked={unused} onChange={(e) => setUnused(e.target.checked)} />
          Unused only
        </label>
        {manage ? (
          <>
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload.mutate(file);
                e.target.value = '';
              }}
            />
            <Button variant="primary" disabled={upload.isPending} onClick={() => fileInput.current?.click()}>
              {upload.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </>
        ) : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Panel title={`${assets.data?.total ?? 0} images`} description="PNG, JPEG, WebP, GIF or SVG, up to 8 MB. An SVG is cleaned of anything that could run before it is stored.">
          <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-3 lg:grid-cols-4">
            {assets.isPending ? <p className="text-sm text-ink-400">Loading…</p> : null}
            {assets.data?.assets.length === 0 ? <p className="col-span-full text-sm text-ink-600">Nothing here yet.</p> : null}
            {(assets.data?.assets ?? []).map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => setSelected(row.id)}
                aria-pressed={selected === row.id}
                className={`rounded border p-2 text-left transition-colors ${selected === row.id ? 'border-accent bg-accent-soft' : 'border-line bg-page hover:border-accent'}`}
              >
                <img src={row.url} alt={row.alt_text ?? ''} className="h-28 w-full rounded object-contain" loading="lazy" />
                <p className="mt-2 truncate text-xs font-medium text-ink-900">{row.title ?? row.original_filename ?? 'Untitled'}</p>
                <p className="text-xs text-ink-400">
                  {row.width_px && row.height_px ? `${row.width_px}×${row.height_px}` : row.content_type.replace('image/', '')} · {size(row.byte_size)}
                </p>
              </button>
            ))}
          </div>
        </Panel>

        {asset ? <Details asset={asset} usages={detail.data?.usages ?? []} manage={manage} categories={categories.data?.categories ?? []} onSave={(input) => update.mutate({ id: asset.id, input })} onDelete={() => remove.mutate(asset.id)} busy={update.isPending || remove.isPending} /> : (
          <Panel title="Details">
            <p className="px-5 py-4 text-sm text-ink-600">Choose an image to see where it is used and to describe it.</p>
          </Panel>
        )}
      </div>
    </div>
  );
}

function Details({
  asset,
  usages,
  manage,
  categories,
  onSave,
  onDelete,
  busy,
}: {
  asset: MediaAsset;
  usages: { usage_kind: string; subject_type: string; subject_id: string; label: string }[];
  manage: boolean;
  categories: { code: string; name: string }[];
  onSave: (input: Record<string, unknown>) => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const [title, setTitle] = useState(asset.title ?? '');
  const [alt, setAlt] = useState(asset.alt_text ?? '');
  const [category, setCategory] = useState(asset.media_category_code ?? '');
  const [credit, setCredit] = useState(asset.credit ?? '');

  // A different asset selected means different fields; keyed by id from the caller.
  return (
    <Panel
      key={asset.id}
      title={asset.title ?? asset.original_filename ?? 'Untitled'}
      description={`${asset.content_type.replace('image/', '').toUpperCase()} · ${asset.width_px ?? '?'}×${asset.height_px ?? '?'} · ${size(asset.byte_size)}`}
    >
      <div className="space-y-4 px-5 py-4">
        <img src={asset.url} alt={asset.alt_text ?? ''} className="max-h-48 w-full rounded border border-line bg-page object-contain p-2" />

        <label className="block text-sm">
          <span className="text-xs font-semibold text-ink-400 uppercase">Name</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} disabled={!manage} className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5" />
        </label>
        <label className="block text-sm">
          <span className="text-xs font-semibold text-ink-400 uppercase">What it shows</span>
          <input
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            disabled={!manage}
            placeholder="A dark corridor lit by one lantern"
            className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5"
          />
          <span className="mt-1 block text-xs text-ink-400">Read aloud to anyone who cannot see the picture. Leave it empty only if the image is pure decoration.</span>
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-xs font-semibold text-ink-400 uppercase">Category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} disabled={!manage} className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5">
              {categories.map((row) => (
                <option key={row.code} value={row.code}>
                  {row.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-xs font-semibold text-ink-400 uppercase">Credit</span>
            <input value={credit} onChange={(e) => setCredit(e.target.value)} disabled={!manage} className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5" />
          </label>
        </div>

        {manage ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" disabled={busy} onClick={() => onSave({ title: title || null, alt_text: alt, media_category_code: category || null, credit: credit || null })}>
              Save
            </Button>
            <Button
              variant="danger"
              disabled={busy || asset.usage_count > 0}
              title={asset.usage_count > 0 ? 'Used somewhere; remove it there first' : undefined}
              onClick={() => {
                if (window.confirm('Delete this image? It cannot be undone.')) onDelete();
              }}
            >
              Delete
            </Button>
          </div>
        ) : null}

        <div>
          <h3 className="text-xs font-semibold text-ink-400 uppercase">Used in</h3>
          {usages.length === 0 ? (
            <p className="mt-1 text-sm text-ink-600">Nowhere yet.</p>
          ) : (
            <ul className="mt-1 space-y-1 text-sm">
              {usages.map((usage) => (
                <li key={`${usage.usage_kind}-${usage.subject_id}`} className="flex items-center gap-2">
                  <Badge tone="accent">{usage.usage_kind.replaceAll('_', ' ').toLowerCase()}</Badge>
                  <span className="text-ink-900">{usage.label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {asset.uploaded_by ? <p className="text-xs text-ink-400">Uploaded by {asset.uploaded_by}</p> : null}
      </div>
    </Panel>
  );
}
